import { userAuthorizationSettings } from './authorization.model'

export type LanguageCode = keyof typeof userAuthorizationSettings.mappings.locales

export type CartoonsAdditionalLanguageCode = typeof cartoonsAdditionalLanguageCodes[number]

export const rbtvAdditionalLanguageCodes = ['en-EU'] as const
export const redbullComAdditionalLanguageCodes = [
  'en-EU',
  'en-MT',
  'he-IL',
  'is-IS',
  'ka-GE',
  'sr-ME',
  'ru-UZ',
  'es-DO',
] as const
export const cartoonsAdditionalLanguageCodes = [
  'ar-BH',
  'ar-DZ',
  'ar-JO',
  'ar-KW',
  'ar-LB',
  'ar-MA',
  'ar-OM',
  'ar-QA',
  'ar-SA',
  'ar-TN',
  'el-CY',
  'en-EG',
  'en-HK',
  'en-JM',
  'en-KW',
  'en-LK',
  'en-MT',
  'en-MV',
  'en-NA',
  'en-OM',
  'es-BO',
  'es-CR',
  'es-DO',
  'es-PA',
  'fr-MU',
  'fr-SN',
  'he-IL',
  'is-IS',
  'ka-GE',
  'pt-AO',
  'ru-BY',
  'ru-UZ',
  'sq-KV',
  'sr-ME',
  'sv-SE',
] as const satisfies readonly LanguageCode[]

export type NonLatinBasedLanguageCode = typeof nonLatinBasedLanguages[number]

export const nonLatinBasedLanguages = [
  'ar-BH',
  'ar-DZ',
  'ar-EG',
  'ar-JO',
  'ar-KW',
  'ar-LB',
  'ar-MA',
  'ar-MEA',
  'ar-OM',
  'ar-QA',
  'ar-SA',
  'ar-TN',
  'az-AZ',
  'bg-BG',
  'el-CY',
  'el-GR',
  'he-IL',
  'hi-IN',
  'ja-JP',
  'ka-GE',
  'ko-KR',
  'mk-MK',
  'ru-BY',
  'ru-KZ',
  'ru-RU',
  'ru-UZ',
  'sr-RS',
  'th-TH',
  'uk-UA',
  'zh-CN',
  'zh-HK',
  'zh-TW',
] as const satisfies readonly LanguageCode[]

export const supportedLanguageCodes = Object.keys(userAuthorizationSettings.mappings.locales) as LanguageCode[]

type ExtractFamilyWithInternationalization<L extends LanguageCode> = L extends `${string}-${string}` ? never : L
type ExtractFamily<L extends LanguageCode> = L extends `${infer Family}-${string}` ? Family : never

export type LocalizationFamily = ExtractFamily<LanguageCode>
export type InternationalLocalizationFamily = ExtractFamilyWithInternationalization<LanguageCode>

export type InternationalLocalization = `${InternationalLocalizationFamily}-INT`

export type FullLanguageCode = Exclude<LanguageCode, InternationalLocalizationFamily> | InternationalLocalization

export const isFullLanguageCode = (str: string): str is FullLanguageCode => {
  const pattern = /^[a-z]{2}-[A-Z]{2,3}$/

  return pattern.test(str)
}

export const isLanguageCode = (v: string): v is LanguageCode => supportedLanguageCodes.includes(v as LanguageCode)

export const isCartoonsAdditionalLanguageCode = (languageCode: string): languageCode is LanguageCode => {
  return cartoonsAdditionalLanguageCodes.includes(languageCode as CartoonsAdditionalLanguageCode)
}

export const languageCodeFromString = (v: string): LanguageCode => {
  if (isLanguageCode(v)) {
    return v
  }

  throw Error(`'${v}' is not a valid language code`)
}

export const isInternationalLocalizationFamily = (languageCode: LanguageCode): languageCode is InternationalLocalizationFamily => {
  return languageCode.length === 2
}

export const isInternationalLocalization = (languageCode: FullLanguageCode): languageCode is InternationalLocalization => {
  return languageCode.endsWith('-INT')
}

export const isNonLatinBasedLanguage = (languageCode: string): languageCode is LanguageCode => {
  return nonLatinBasedLanguages.includes(languageCode as NonLatinBasedLanguageCode)
}
